import { useCallback, useState } from 'react';
import type { StickerItem } from '@reactvideoeditor/react-video-editor/types';
import {
  ASPECTLY_QR_DEFAULT_URL,
  buildAspectlyQrStickerContent,
  isAspectlyQrStickerContent,
  parseAspectlyQrStickerContent,
} from '@/rve/aspectlyQrStickerContent';

export interface AspectlyQrStickerSettingsPanelProps {
  localOverlay: StickerItem;
  setLocalOverlay: (item: StickerItem) => void;
}

/** URL editor for selected QR stickers (patched into RVE StickerSettingsPanel). */
export function AspectlyQrStickerSettingsPanel({
  localOverlay,
  setLocalOverlay,
}: AspectlyQrStickerSettingsPanelProps) {
  const parsed = parseAspectlyQrStickerContent(localOverlay.content);
  const [url, setUrl] = useState(parsed?.url ?? ASPECTLY_QR_DEFAULT_URL);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyUrl = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const content = await buildAspectlyQrStickerContent(url);
      setLocalOverlay({ ...localOverlay, content });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update QR');
    } finally {
      setBusy(false);
    }
  }, [localOverlay, setLocalOverlay, url]);

  if (!isAspectlyQrStickerContent(localOverlay.content)) {
    return (
      <p className="rve:text-sm rve:text-muted-foreground">
        Select a QR Code sticker to edit its destination URL.
      </p>
    );
  }

  return (
    <div className="rve:space-y-3">
      <label className="rve:flex rve:flex-col rve:gap-1.5">
        <span className="rve:text-xs rve:font-medium rve:text-foreground">
          Destination URL
        </span>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="rve:w-full rve:rounded-md rve:border rve:border-input rve:bg-background rve:px-2 rve:py-1.5 rve:text-sm rve:text-foreground"
          placeholder={ASPECTLY_QR_DEFAULT_URL}
        />
      </label>
      {error ? (
        <p className="rve:text-xs rve:text-destructive">{error}</p>
      ) : null}
      <button
        type="button"
        disabled={busy}
        onClick={() => void applyUrl()}
        className="rve:rounded-md rve:bg-primary rve:px-3 rve:py-1.5 rve:text-xs rve:font-medium rve:text-primary-foreground rve:disabled:opacity-50"
      >
        {busy ? 'Updating…' : 'Apply URL'}
      </button>
    </div>
  );
}
